import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { favoritesApi, type ServerFavoriteItem } from "@/hooks/use-favorites-mutations";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export type FavoriteItem = ServerFavoriteItem;

/** "loading" until the first GET /favorites settles; "error" only when that GET failed. */
export type FavoritesStatus = "loading" | "ready" | "error";

interface FavoritesContextType {
  items: FavoriteItem[];
  status: FavoritesStatus;
  /** Re-runs the initial load after an error. */
  retry: () => void;
  addItem: (item: FavoriteItem) => void;
  removeItem: (id: string) => void;
  isFavorite: (id: string) => boolean;
  toggleFavorite: (item: FavoriteItem) => void;
  /** Empties the list and returns what was in it, for an undo. */
  clearFavorites: () => FavoriteItem[];
  /** Puts back items removed by clearFavorites (the undo). */
  restoreItems: (items: FavoriteItem[]) => void;
  syncWithServer: () => Promise<void>;
  resetFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<FavoriteItem[]>([]);
  const [status, setStatus] = useState<FavoritesStatus>("loading");
  const { isAuthenticated, isLoading } = useAuth();

  const fetchAndSetFavorites = useCallback(async () => {
    const res = await favoritesApi.getFavorites();
    setItems(res.items);
    setStatus("ready");
  }, []);

  // A failed optimistic toggle would otherwise leave the heart showing a state the server
  // never saved; re-reading the list puts it back.
  const resyncAfterFailure = useCallback(
    (message: string) => {
      toast.error(message);
      fetchAndSetFavorites().catch(() => {});
    },
    [fetchAndSetFavorites],
  );

  const addItem = useCallback(
    (item: FavoriteItem) => {
      setItems((prev) => {
        if (prev.find((i) => i.id === item.id)) return prev;
        return [...prev, item];
      });
      favoritesApi
        .addItem(item.id)
        .catch(() => resyncAfterFailure("Не удалось добавить в избранное"));
    },
    [resyncAfterFailure],
  );

  const removeItem = useCallback(
    (id: string) => {
      setItems((prev) => prev.filter((item) => item.id !== id));
      favoritesApi
        .removeItem(id)
        .catch(() => resyncAfterFailure("Не удалось убрать из избранного"));
    },
    [resyncAfterFailure],
  );

  // Set lookup so a grid of cards does not scan the array once per card.
  const favoriteIds = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  const isFavorite = useCallback((id: string) => favoriteIds.has(id), [favoriteIds]);

  const toggleFavorite = useCallback(
    (item: FavoriteItem) => {
      if (favoriteIds.has(item.id)) {
        removeItem(item.id);
      } else {
        addItem(item);
      }
    },
    [favoriteIds, addItem, removeItem],
  );

  // The undo has to wait for the DELETE, or a fast "Отменить" could re-add items the
  // in-flight clear then wipes again.
  const pendingClear = useRef<Promise<unknown>>(Promise.resolve());

  const clearFavorites = useCallback(() => {
    const removed = items;
    setItems([]);
    pendingClear.current = favoritesApi
      .clearFavorites()
      .catch(() => resyncAfterFailure("Не удалось очистить избранное"));
    return removed;
  }, [items, resyncAfterFailure]);

  const restoreItems = useCallback(
    (restored: FavoriteItem[]) => {
      setItems((prev) => [...restored.filter((r) => !prev.some((i) => i.id === r.id)), ...prev]);
      pendingClear.current
        .then(() => Promise.all(restored.map((i) => favoritesApi.addItem(i.id))))
        .catch(() => resyncAfterFailure("Не удалось восстановить избранное"));
    },
    [resyncAfterFailure],
  );

  const syncWithServer = fetchAndSetFavorites;

  const resetFavorites = useCallback(() => {
    setItems([]);
    setStatus("ready");
  }, []);

  const loadFavorites = useCallback(() => {
    setStatus("loading");
    fetchAndSetFavorites().catch(() => setStatus("error"));
  }, [fetchAndSetFavorites]);

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      loadFavorites();
    } else {
      resetFavorites();
    }
  }, [isAuthenticated, isLoading, loadFavorites, resetFavorites]);

  // Memoised: an unstable value re-renders every consumer (i.e. every product card
  // in the grid) on each favourites change, defeating the memo() on the cards.
  const value = useMemo(
    () => ({
      items,
      status,
      retry: loadFavorites,
      addItem,
      removeItem,
      isFavorite,
      toggleFavorite,
      clearFavorites,
      restoreItems,
      syncWithServer,
      resetFavorites,
    }),
    [
      items,
      status,
      loadFavorites,
      addItem,
      removeItem,
      isFavorite,
      toggleFavorite,
      clearFavorites,
      restoreItems,
      syncWithServer,
      resetFavorites,
    ],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites() {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
}
